/* Price Match Machine — storefront behavior.
   Self-contained companion to sections/price-match-machine.liquid.
   No dependencies; uses Shopify's predictive search JSON API for product
   lookup and /products/{handle}.js for authoritative variant prices.
   The claim itself submits through the native Shopify contact form. */

(function () {
  'use strict';

  var root = document.querySelector('.pm');
  if (!root) return;

  var fmt = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' });
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ─── Hero vignette: our tag ticks down to theirs, stamp slams in ──── */

  var scene = root.querySelector('[data-pm-scene]');
  if (scene) {
    var scenePrice = scene.querySelector('[data-pm-scene-price]');
    var sceneStamp = scene.querySelector('[data-pm-scene-stamp]');
    var sceneFrom = parseFloat(scenePrice.dataset.from);
    var sceneTo = parseFloat(scenePrice.dataset.to);
    var scenePlaying = false;

    var playScene = function () {
      if (reducedMotion) {
        scenePrice.textContent = fmt.format(sceneTo);
        sceneStamp.hidden = false;
        return;
      }
      if (scenePlaying) return;
      scenePlaying = true;
      sceneStamp.hidden = true;
      scenePrice.textContent = fmt.format(sceneFrom);
      setTimeout(function () {
        var start = null;
        var DURATION = 900;
        var step = function (ts) {
          if (start === null) start = ts;
          var t = Math.min((ts - start) / DURATION, 1);
          var eased = 1 - Math.pow(1 - t, 3);
          scenePrice.textContent = fmt.format(sceneFrom + (sceneTo - sceneFrom) * eased);
          if (t < 1) {
            requestAnimationFrame(step);
          } else {
            setTimeout(function () {
              sceneStamp.hidden = false;
              scenePlaying = false;
            }, 150);
          }
        };
        requestAnimationFrame(step);
      }, 500);
    };

    playScene();
    scene.addEventListener('click', playScene);
  }

  /* ─── Confirmation page: fill the stamped receipt and stop ─────────── */

  var confirmEl = root.querySelector('#pm-confirmation');
  if (confirmEl) {
    try {
      var saved = JSON.parse(sessionStorage.getItem('pm-claim') || 'null');
      if (saved && saved.ref) {
        var refEl = confirmEl.querySelector('[data-pm-confirm="ref"]');
        refEl.textContent = 'REF ' + saved.ref;
        refEl.hidden = false;
        var lines = confirmEl.querySelector('[data-pm-confirm="lines"]');
        confirmEl.querySelector('[data-pm-confirm="product"]').textContent = saved.product;
        confirmEl.querySelector('[data-pm-confirm="ours"]').textContent = saved.ours;
        confirmEl.querySelector('[data-pm-confirm="theirs"]').textContent = saved.theirs;
        confirmEl.querySelector('[data-pm-confirm="save"]').textContent = saved.save;
        lines.hidden = false;
      }
    } catch (e) {
      /* receipt still shows the generic confirmation */
    }
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { confirmEl.removeAttribute('data-pm-entering'); });
    });
    confirmEl.scrollIntoView({ block: 'center' });
    confirmEl.focus({ preventScroll: true });
    return;
  }

  /* ─── Machine state ────────────────────────────────────────────────── */

  var form = root.querySelector('#pm-form');
  if (!form) return;

  var predictiveUrl = root.dataset.predictiveUrl || '/search/suggest';

  var els = {
    q: root.querySelector('#pm-q'),
    search: root.querySelector('.pm-search'),
    results: root.querySelector('#pm-results'),
    picked: root.querySelector('.pm-picked'),
    pickedImg: root.querySelector('.pm-picked__img'),
    pickedTitle: root.querySelector('.pm-picked__title'),
    pickedPrice: root.querySelector('.pm-picked__price'),
    change: root.querySelector('.pm-picked__change'),
    variantWrap: root.querySelector('.pm-variantwrap'),
    variantSelect: root.querySelector('#pm-variant'),
    stepOffer: root.querySelector('[data-pm-step="offer"]'),
    stepChecks: root.querySelector('[data-pm-step="checks"]'),
    url: root.querySelector('#pm-url'),
    price: root.querySelector('#pm-price'),
    calloutMarketplace: root.querySelector('[data-pm-callout="marketplace"]'),
    calloutNotLower: root.querySelector('[data-pm-callout="notlower"]'),
    notLowerLink: root.querySelector('[data-pm-notlower-link]'),
    checks: Array.prototype.slice.call(root.querySelectorAll('.pm-check')),
    status: root.querySelector('.pm-status'),
    ledger: root.querySelector('[data-pm-ledger]'),
    ledgerProduct: root.querySelector('[data-pm-ledger-product]'),
    ledgerOurs: root.querySelector('[data-pm-ledger-ours]'),
    ledgerTheirs: root.querySelector('[data-pm-ledger-theirs]'),
    ledgerMatched: root.querySelector('[data-pm-ledger-matched]'),
    ledgerSave: root.querySelector('[data-pm-ledger-save]'),
    claim: root.querySelector('[data-pm-claim]'),
    submit: root.querySelector('.pm-submit')
  };

  var state = {
    product: null,   // { title, url, image }
    variants: [],    // available variants from /products/{handle}.js
    variant: null,   // chosen variant { title, price } (price in cents)
    lastSave: 0
  };

  /* ─── Helpers ──────────────────────────────────────────────────────── */

  function debounce(fn, ms) {
    var t;
    return function () {
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(null, args); }, ms);
    };
  }

  function parsePrice(value) {
    var n = parseFloat(String(value).replace(/[$,\s]/g, ''));
    return isFinite(n) && n > 0 ? n : null;
  }

  function parseCompetitorUrl(value) {
    var v = String(value).trim();
    if (!v) return null;
    try {
      var u = new URL(/^https?:\/\//i.test(v) ? v : 'https://' + v);
      return u.hostname.indexOf('.') > 0 ? u : null;
    } catch (e) {
      return null;
    }
  }

  function isMarketplace(url) {
    return /(^|\.)(ebay|amazon)\./i.test(url.hostname);
  }

  function setHidden(el, hidden) {
    if (el.hidden === hidden) return;
    if (!hidden && el.classList.contains('pm-anim')) {
      el.setAttribute('data-pm-entering', '');
      el.hidden = false;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { el.removeAttribute('data-pm-entering'); });
      });
    } else {
      el.hidden = hidden;
    }
  }

  function imageUrl(src, width) {
    if (!src) return '';
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  /* ─── Step 1: predictive product search ────────────────────────────── */

  var searchAbort = null;
  var activeIndex = -1;
  var currentResults = [];

  function closeResults() {
    els.results.hidden = true;
    els.results.innerHTML = '';
    els.q.setAttribute('aria-expanded', 'false');
    activeIndex = -1;
    currentResults = [];
  }

  function renderResults(products) {
    currentResults = products;
    activeIndex = -1;
    if (!products.length) {
      els.results.innerHTML = '<li class="pm-results__empty">No matches in our shop. Try another spelling or a shorter search.</li>';
    } else {
      els.results.innerHTML = products.map(function (p, i) {
        var img = p.featured_image && p.featured_image.url ? p.featured_image.url : (p.image || '');
        var imgHtml = img
          ? '<img class="pm-result__img" src="' + imageUrl(img, 80) + '" alt="" width="40" height="40" loading="lazy">'
          : '<span class="pm-result__img" aria-hidden="true"></span>';
        return (
          '<li role="presentation">' +
            '<button type="button" class="pm-result" role="option" id="pm-opt-' + i + '" data-index="' + i + '" aria-selected="false">' +
              imgHtml +
              '<span>' +
                '<span class="pm-result__title"></span>' +
                '<span class="pm-result__meta"></span>' +
              '</span>' +
            '</button>' +
          '</li>'
        );
      }).join('');
      // textContent assignment keeps product titles safely escaped
      var buttons = els.results.querySelectorAll('.pm-result');
      products.forEach(function (p, i) {
        buttons[i].querySelector('.pm-result__title').textContent = p.title;
        buttons[i].querySelector('.pm-result__meta').textContent =
          (p.vendor ? p.vendor + ' · ' : '') + '$' + p.price;
      });
    }
    els.results.hidden = false;
    els.q.setAttribute('aria-expanded', 'true');
  }

  function setActive(index) {
    var options = els.results.querySelectorAll('.pm-result');
    if (!options.length) return;
    activeIndex = (index + options.length) % options.length;
    options.forEach(function (opt, i) {
      opt.setAttribute('aria-selected', i === activeIndex ? 'true' : 'false');
    });
    els.q.setAttribute('aria-activedescendant', 'pm-opt-' + activeIndex);
    options[activeIndex].scrollIntoView({ block: 'nearest' });
  }

  var fetchSuggestions = debounce(function (terms) {
    if (searchAbort) searchAbort.abort();
    searchAbort = new AbortController();
    var params =
      '?q=' + encodeURIComponent(terms) +
      '&resources[type]=product&resources[limit]=6' +
      '&resources[options][unavailable_products]=hide';
    fetch(predictiveUrl + '.json' + params, { signal: searchAbort.signal })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        var products = data && data.resources && data.resources.results && data.resources.results.products;
        renderResults(products || []);
      })
      .catch(function () { /* aborted or offline — keep quiet */ });
  }, 250);

  els.q.addEventListener('input', function () {
    var terms = els.q.value.trim();
    if (terms.length < 2) { closeResults(); return; }
    fetchSuggestions(terms);
  });

  els.q.addEventListener('keydown', function (e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(activeIndex + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(activeIndex - 1); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && currentResults[activeIndex]) pickProduct(currentResults[activeIndex]);
    } else if (e.key === 'Escape') {
      closeResults();
    }
  });

  els.results.addEventListener('click', function (e) {
    var btn = e.target.closest('.pm-result');
    if (btn) pickProduct(currentResults[parseInt(btn.dataset.index, 10)]);
  });

  document.addEventListener('click', function (e) {
    if (!els.search.contains(e.target)) closeResults();
  });

  function pickProduct(product) {
    state.product = {
      title: product.title,
      url: product.url.split('?')[0],
      image: product.featured_image && product.featured_image.url ? product.featured_image.url : (product.image || '')
    };
    state.variants = [];
    state.variant = null;
    closeResults();
    els.search.hidden = true;
    els.picked.hidden = false;
    if (state.product.image) {
      els.pickedImg.src = imageUrl(state.product.image, 112);
      els.pickedImg.hidden = false;
    } else {
      els.pickedImg.removeAttribute('src');
      els.pickedImg.hidden = true;
    }
    els.pickedImg.alt = state.product.title;
    els.pickedTitle.textContent = state.product.title;
    els.pickedPrice.textContent = 'Checking our price…';
    els.variantWrap.hidden = true;

    fetch(state.product.url + '.js')
      .then(function (res) { return res.json(); })
      .then(function (data) {
        var available = (data.variants || []).filter(function (v) { return v.available; });
        state.variants = available;
        if (!available.length) {
          els.pickedPrice.textContent = 'Currently out of stock with us. Matches need stock at both stores.';
          refresh();
          return;
        }
        if (available.length === 1) {
          state.variant = available[0];
          els.pickedPrice.textContent = 'Our price: ' + fmt.format(available[0].price / 100);
          refresh();
          return;
        }
        var min = Math.min.apply(null, available.map(function (v) { return v.price; }));
        els.pickedPrice.textContent = 'From ' + fmt.format(min / 100) + '. Pick your size below.';
        els.variantSelect.innerHTML =
          '<option value="">Choose size / colour…</option>' +
          available.map(function (v, i) {
            return '<option value="' + i + '"></option>';
          }).join('');
        var options = els.variantSelect.querySelectorAll('option');
        available.forEach(function (v, i) {
          options[i + 1].textContent = v.title + ' · ' + fmt.format(v.price / 100);
        });
        els.variantWrap.hidden = false;
        refresh();
      })
      .catch(function () {
        els.pickedPrice.textContent = 'Couldn’t load our price. Please try again.';
      });
  }

  els.change.addEventListener('click', function () {
    state.product = null;
    state.variants = [];
    state.variant = null;
    els.picked.hidden = true;
    els.variantWrap.hidden = true;
    els.search.hidden = false;
    els.q.value = '';
    els.q.focus();
    refresh();
  });

  els.variantSelect.addEventListener('change', function () {
    var i = parseInt(els.variantSelect.value, 10);
    state.variant = isNaN(i) ? null : state.variants[i];
    if (state.variant) {
      els.pickedPrice.textContent = 'Our price: ' + fmt.format(state.variant.price / 100);
    }
    refresh();
  });

  /* ─── Steps 2 + 3: inputs and checks ───────────────────────────────── */

  els.url.addEventListener('input', refresh);
  els.price.addEventListener('input', refresh);
  els.checks.forEach(function (check) {
    check.querySelectorAll('input[type="radio"]').forEach(function (radio) {
      radio.addEventListener('change', refresh);
    });
  });

  /* ─── The reveal: recompute everything on any change ───────────────── */

  function animateSave(target) {
    if (reducedMotion || Math.abs(target - state.lastSave) < 0.01) {
      els.ledgerSave.textContent = fmt.format(target);
      state.lastSave = target;
      return;
    }
    var from = state.lastSave;
    var start = null;
    var DURATION = 400;
    function tick(ts) {
      if (start === null) start = ts;
      var t = Math.min((ts - start) / DURATION, 1);
      var eased = 1 - Math.pow(1 - t, 3);
      els.ledgerSave.textContent = fmt.format(from + (target - from) * eased);
      if (t < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
    state.lastSave = target;
  }

  function refresh() {
    var ours = state.variant ? state.variant.price / 100 : null;
    var theirs = parsePrice(els.price.value);
    var url = parseCompetitorUrl(els.url.value);
    var marketplace = url ? isMarketplace(url) : false;

    setHidden(els.calloutMarketplace, !marketplace);

    var notLower = ours !== null && theirs !== null && theirs >= ours;
    setHidden(els.calloutNotLower, !notLower);
    if (notLower && state.product) els.notLowerLink.href = state.product.url;

    var answers = els.checks.map(function (check) {
      var checked = check.querySelector('input[type="radio"]:checked');
      check.querySelector('.pm-check__fail').hidden = !(checked && checked.value === 'no');
      return checked ? checked.value : null;
    });
    var anyNo = answers.indexOf('no') !== -1;
    var allYes = answers.length > 0 && answers.every(function (a) { return a === 'yes'; });

    if (anyNo) {
      els.status.textContent = 'Heads up: as answered, this one wouldn’t qualify. The conditions marked in red are required for a match.';
      els.status.hidden = false;
    } else {
      els.status.hidden = true;
    }

    var offerValid = url !== null && !marketplace && theirs !== null;
    var showLedger = state.variant !== null && offerValid && theirs < ours;

    // Wizard sequencing: each step appears once the previous one is complete,
    // and collapses again if the shopper goes back and breaks its inputs.
    setHidden(els.stepOffer, state.variant === null);
    setHidden(els.stepChecks, !showLedger);

    if (showLedger) {
      var save = ours - theirs;
      var variantLabel = state.variant.title === 'Default Title' ? '' : ', ' + state.variant.title;
      els.ledgerProduct.textContent = state.product.title + variantLabel;
      els.ledgerOurs.textContent = fmt.format(ours);
      els.ledgerTheirs.textContent = fmt.format(theirs);
      els.ledgerMatched.textContent = fmt.format(theirs);
      animateSave(save);
    } else {
      state.lastSave = 0;
    }
    setHidden(els.ledger, !showLedger);

    var eligible = showLedger && allYes;
    setHidden(els.claim, !eligible);
    els.submit.disabled = !eligible;

    if (eligible) {
      var setField = function (name, value) {
        root.querySelector('[data-pm-field="' + name + '"]').value = value;
      };
      setField('product', state.product.title);
      setField('variant', state.variant.title);
      setField('ours', fmt.format(ours) + ' CAD');
      setField('theirs', fmt.format(theirs) + ' CAD');
      setField('url', url.href);
      setField('save', fmt.format(ours - theirs) + ' CAD');
      setField('checks', answers.length + '/' + answers.length + ' conditions confirmed by customer');
    }
  }

  /* ─── Submit: claim ref + receipt data for the confirmation page ───── */

  form.addEventListener('submit', function (e) {
    if (els.submit.disabled) { e.preventDefault(); return; }
    var now = new Date();
    var pad = function (n) { return String(n).padStart(2, '0'); };
    var ref = 'PM-' +
      String(now.getFullYear()).slice(2) + pad(now.getMonth() + 1) + pad(now.getDate()) +
      '-' + String(Math.floor(1000 + Math.random() * 9000));
    root.querySelector('[data-pm-field="ref"]').value = ref;
    var variantLabel = state.variant.title === 'Default Title' ? '' : ', ' + state.variant.title;
    try {
      sessionStorage.setItem('pm-claim', JSON.stringify({
        ref: ref,
        product: state.product.title + variantLabel,
        ours: fmt.format(state.variant.price / 100),
        theirs: fmt.format(parsePrice(els.price.value)),
        save: fmt.format(state.variant.price / 100 - parsePrice(els.price.value))
      }));
    } catch (err) {
      /* private browsing — confirmation falls back to generic text */
    }
  });
})();
